var _jsxFileName = "/var/home/clovis/Documents/Mangane/app/soapbox/components/status.tsx";
/* eslint-disable jsx-a11y/interactive-supports-focus */
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HotKeys } from 'react-hotkeys';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { NavLink, useHistory } from 'react-router-dom';
import { mentionCompose, replyComposeWithConfirmation } from 'soapbox/actions/compose';
import { toggleFavourite, toggleReblog } from 'soapbox/actions/interactions';
import { openModal } from 'soapbox/actions/modals';
import { toggleStatusHidden } from 'soapbox/actions/statuses';
import Icon from 'soapbox/components/icon';
import AccountContainer from 'soapbox/containers/account_container';
import QuotedStatus from 'soapbox/features/status/containers/quoted_status_container';
import { useAppDispatch, useSettings, useLogo } from 'soapbox/hooks';
import { defaultMediaVisibility, textForScreenReader, getActualStatus } from 'soapbox/utils/status';
import { ProfilePopper } from './account';
import HoverRefWrapper from './hover_ref_wrapper';
import StatusActionBar from './status-action-bar';
import StatusMedia from './status-media';
import StatusReplyMentions from './status-reply-mentions';
import StatusContent from './status_content';
import { HStack, Text } from './ui';

// Defined in components/scrollable_list

const messages = defineMessages({
  reblogged_by: {
    "id": "status.reblogged_by",
    "defaultMessage": "{name} reposted"
  }
});
const Status = props => {
  const {
    status,
    focusable = true,
    hoverable = true,
    onClick,
    onMoveUp,
    onMoveDown,
    muted,
    hidden,
    featured,
    unread,
    group,
    hideActionBar,
    withDismiss,
    timeline = false
  } = props;
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const settings = useSettings();
  const displayMedia = settings.get('displayMedia');
  const didShowCard = useRef(false);
  const node = useRef(null);
  const [showMedia, setShowMedia] = useState(defaultMediaVisibility(status, displayMedia));
  const actualStatus = useMemo(() => getActualStatus(status), [status]);
  const logo = useLogo();

  // Track height changes we know about to compensate scrolling.
  useEffect(() => {
    didShowCard.current = Boolean(!muted && !hidden && status?.card);
  }, [muted, hidden, status]);
  useEffect(() => {
    setShowMedia(defaultMediaVisibility(status, displayMedia));
  }, [displayMedia, status, status.id]);
  const handleToggleMediaVisibility = useCallback(() => {
    setShowMedia(!showMedia);
  }, [showMedia]);
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      history.push(`/@${actualStatus.getIn(['account', 'acct'])}/posts/${actualStatus.id}`);
    }
  };
  const handleExpandedToggle = () => {
    dispatch(toggleStatusHidden(actualStatus));
  };
  const handleHotkeyOpenMedia = useCallback(e => {
    const status = actualStatus;
    const firstAttachment = status.media_attachments.first();
    e?.preventDefault();
    if (firstAttachment) {
      if (firstAttachment.type === 'video') {
        dispatch(openModal('VIDEO', {
          media: firstAttachment,
          time: 0
        }));
      } else {
        dispatch(openModal('MEDIA', {
          media: status.media_attachments,
          index: 0
        }));
      }
    }
  }, [actualStatus, dispatch]);
  const handleHotkeyReply = useCallback(e => {
    e?.preventDefault();
    dispatch(replyComposeWithConfirmation(actualStatus, intl));
  }, [actualStatus, dispatch, intl]);
  const handleHotkeyFavourite = useCallback(() => {
    toggleFavourite(actualStatus);
  }, [actualStatus]);
  const handleHotkeyBoost = useCallback(e => {
    const modalReblog = () => dispatch(toggleReblog(actualStatus));
    const boostModal = settings.get('boostModal');
    if (e && e.shiftKey || !boostModal) {
      modalReblog();
    } else {
      dispatch(openModal('BOOST', {
        status: actualStatus,
        onReblog: modalReblog
      }));
    }
  }, [actualStatus, dispatch, settings]);
  const handleHotkeyMention = useCallback(e => {
    e?.preventDefault();
    dispatch(mentionCompose(actualStatus.account));
  }, [actualStatus.account, dispatch]);
  const handleHotkeyOpen = useCallback(() => {
    history.push(`/@${actualStatus.getIn(['account', 'acct'])}/posts/${actualStatus.id}`);
  }, [actualStatus, history]);
  const handleHotkeyOpenProfile = useCallback(() => {
    history.push(`/@${actualStatus.getIn(['account', 'acct'])}`);
  }, [actualStatus, history]);
  const handleHotkeyMoveUp = useCallback(e => {
    if (onMoveUp) {
      onMoveUp(status.id, featured);
    }
  }, [featured, onMoveUp, status.id]);
  const handleHotkeyMoveDown = useCallback(e => {
    if (onMoveDown) {
      onMoveDown(status.id, featured);
    }
  }, [featured, onMoveDown, status.id]);
  const handleHotkeyToggleHidden = useCallback(() => {
    dispatch(toggleStatusHidden(actualStatus));
  }, [actualStatus, dispatch]);
  const handleHotkeyToggleSensitive = useCallback(() => {
    handleToggleMediaVisibility();
  }, [handleToggleMediaVisibility]);
  const handleHotkeyReact = useCallback(() => {
    _expandEmojiSelector();
  }, []);
  const _expandEmojiSelector = () => {
    const firstEmoji = node.current?.querySelector('.emoji-react-selector .emoji-react-selector__emoji');
    firstEmoji?.focus();
  };
  const privacyIcon = React.useMemo(() => {
    switch (actualStatus?.visibility) {
      default:
      case 'public':
        return require('@tabler/icons/world.svg');
      case 'unlisted':
        return require('@tabler/icons/eye-off.svg');
      case 'local':
        return logo;
      case 'private':
        return require('@tabler/icons/lock.svg');
      case 'direct':
        return require('@tabler/icons/mail.svg');
    }
  }, [actualStatus?.visibility, logo]);
  const quote = useMemo(() => {
    if (actualStatus.quote) {
      if (actualStatus.pleroma.get('quote_visible', true) === false) {
        return /*#__PURE__*/React.createElement("div", {
          className: "quoted-status-tombstone",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 207,
            columnNumber: 11
          }
        }, /*#__PURE__*/React.createElement("p", {
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 208,
            columnNumber: 13
          }
        }, /*#__PURE__*/React.createElement(FormattedMessage, {
          id: "statuses.quote_tombstone",
          defaultMessage: "Post is unavailable.",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 208,
            columnNumber: 16
          }
        })));
      } else {
        return /*#__PURE__*/React.createElement(QuotedStatus, {
          statusId: actualStatus.quote,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 212,
            columnNumber: 16
          }
        });
      }
    }
  }, [actualStatus.pleroma, actualStatus.quote]);
  const handlers = useMemo(() => muted ? undefined : {
    reply: handleHotkeyReply,
    favourite: handleHotkeyFavourite,
    boost: handleHotkeyBoost,
    mention: handleHotkeyMention,
    open: handleHotkeyOpen,
    openProfile: handleHotkeyOpenProfile,
    moveUp: handleHotkeyMoveUp,
    moveDown: handleHotkeyMoveDown,
    toggleHidden: handleHotkeyToggleHidden,
    toggleSensitive: handleHotkeyToggleSensitive,
    openMedia: handleHotkeyOpenMedia,
    react: handleHotkeyReact
  }, [handleHotkeyBoost, handleHotkeyFavourite, handleHotkeyMention, handleHotkeyMoveDown, handleHotkeyMoveUp, handleHotkeyOpen, handleHotkeyOpenMedia, handleHotkeyOpenProfile, handleHotkeyReact, handleHotkeyReply, handleHotkeyToggleHidden, handleHotkeyToggleSensitive, muted]);
  const statusUrl = `/@${actualStatus.getIn(['account', 'acct'])}/posts/${actualStatus.id}`;
  if (!status) return null;
  if (hidden) {
    return /*#__PURE__*/React.createElement("div", {
      ref: node,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 240,
        columnNumber: 7
      }
    }, actualStatus.getIn(['account', 'display_name']) || actualStatus.getIn(['account', 'username']), actualStatus.content);
  }
  return /*#__PURE__*/React.createElement(HotKeys, {
    handlers: handlers,
    "data-testid": "status",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 249,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('status cursor-pointer ', {
      focusable
    }),
    tabIndex: focusable && !muted ? 0 : undefined,
    "data-featured": featured ? 'true' : null,
    "aria-label": textForScreenReader(intl, actualStatus, intl.formatMessage(messages.reblogged_by, {
      name: String(status.getIn(['account', 'acct']))
    })),
    ref: node,
    onClick: () => history.push(statusUrl),
    role: "link",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 250,
      columnNumber: 7
    }
  }, featured && /*#__PURE__*/React.createElement("div", {
    className: "pt-4 px-4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 263,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    space: 1,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 264,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: require('@tabler/icons/pinned.svg'),
    className: "text-gray-600 dark:text-gray-400",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 265,
      columnNumber: 15
    }
  }), /*#__PURE__*/React.createElement(Text, {
    size: "sm",
    theme: "muted",
    weight: "medium",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 267,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "status.pinned",
    defaultMessage: "Pinned post",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 268,
      columnNumber: 17
    }
  })))), /*#__PURE__*/React.createElement("div", {
    className: classNames('status__wrapper', `status-${actualStatus.visibility}`, {
      'from-primary-500/10 dark:from-primary-200/10 to-white dark:via-slate-800 dark:to-slate-800': timeline,
      'status-reply': !!status.in_reply_to_id,
      muted,
      read: unread === false
    }),
    "data-id": status.id,
    style: timeline ? {
      background: 'linear-gradient(to bottom, var(--tw-gradient-from) 0px, var(--tw-gradient-to) 50px, var(--tw-gradient-to) 100%)'
    } : undefined,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 274,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('flex items-center', {
      'mb-3': status.reblog && typeof status.reblog === 'object'
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 289,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "grow min-w-0",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 290,
      columnNumber: 13
    }
  }, status.reblog && typeof status.reblog === 'object' && /*#__PURE__*/React.createElement(ProfilePopper, {
    condition: true,
    wrapper: children => /*#__PURE__*/React.createElement(HoverRefWrapper, {
      className: "relative",
      accountId: status.getIn(['account', 'id']),
      inline: true,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 295,
        columnNumber: 44
      }
    }, children),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 293,
      columnNumber: 19
    }
  }, /*#__PURE__*/React.createElement(NavLink, {
    to: `/@${status.getIn(['account', 'acct'])}`,
    onClick: event => event.stopPropagation(),
    className: "flex items-center text-gray-700 dark:text-gray-600 text-xs font-medium space-x-1 hover:underline",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 297,
      columnNumber: 21
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: require('@tabler/icons/repeat.svg'),
    className: "text-green-600",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 302,
      columnNumber: 23
    }
  }), /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 304,
      columnNumber: 23
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "status.reblogged_by",
    defaultMessage: "{name} reposted",
    values: {
      name: /*#__PURE__*/React.createElement("bdi", {
        className: "max-w-[100px] truncate pr-1",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 309,
          columnNumber: 35
        }
      }, /*#__PURE__*/React.createElement("strong", {
        className: "text-gray-800 dark:text-gray-200",
        dangerouslySetInnerHTML: {
          __html: String(status.getIn(['account', 'display_name_html']))
        },
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 310,
          columnNumber: 31
        }
      }))
    },
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 305,
      columnNumber: 25
    }
  }))))), /*#__PURE__*/React.createElement(Icon, {
    "aria-hidden": true,
    src: privacyIcon,
    className: "h-5 w-5 shrink-0 text-gray-400 dark:text-gray-600",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 320,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-3",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 322,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(AccountContainer, {
    key: String(actualStatus.getIn(['account', 'id'])),
    id: String(actualStatus.getIn(['account', 'id'])),
    timestamp: actualStatus.created_at,
    timestampUrl: statusUrl,
    hideActions: true,
    showEdit: !!actualStatus.edited_at,
    showProfileHoverCard: hoverable,
    withLinkToProfile: hoverable,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 323,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "status__content-wrapper",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 335,
      columnNumber: 11
    }
  }, !group && actualStatus.group && /*#__PURE__*/React.createElement("div", {
    className: "status__meta",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 337,
      columnNumber: 15
    }
  }, "Posted in ", /*#__PURE__*/React.createElement(NavLink, {
    to: `/groups/${actualStatus.getIn(['group', 'id'])}`,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 338,
      columnNumber: 27
    }
  }, String(actualStatus.getIn(['group', 'title'])))), /*#__PURE__*/React.createElement(StatusReplyMentions, {
    status: actualStatus,
    hoverable: hoverable,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 342,
      columnNumber: 13
    }
  }), /*#__PURE__*/React.createElement(StatusContent, {
    status: actualStatus,
    onClick: handleClick,
    expanded: !actualStatus.hidden,
    onExpandedToggle: handleExpandedToggle,
    collapsable: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 347,
      columnNumber: 13
    }
  }), !actualStatus.hidden && /*#__PURE__*/React.createElement("div", {
    className: "pt-1",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 357,
      columnNumber: 17
    }
  }, /*#__PURE__*/React.createElement(StatusMedia, {
    status: actualStatus,
    muted: muted,
    onClick: handleClick,
    showMedia: showMedia,
    onToggleVisibility: handleToggleMediaVisibility,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 358,
      columnNumber: 19
    }
  }), quote), !hideActionBar && /*#__PURE__*/React.createElement("div", {
    className: "pt-4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 372,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(StatusActionBar, {
    status: actualStatus,
    withDismiss: withDismiss,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 373,
      columnNumber: 17
    }
  }))))));
};
export default Status;